import { IconInfoCircle } from '@tabler/icons-react'
import { Skeleton, Table, Tooltip, Typography } from 'antd'
import { ColumnType } from 'antd/lib/table'
import styled from 'styled-components'

import {
  calculateHours,
  calculateTotalHours,
} from '../CustomBlocks/WorkerAttendance/workerAttendanceHelpers'

import { useTemplateRenderer } from '@/context/templateRendererContext'
import { ManualSignOns, useAttendanceData } from '@/hooks/useAttendanceData'
import { SubcontractorSummaryDisplayBlock as SubcontractorSummaryDisplayBlockType } from '@/modules/templates/types/blueprint'

import { Subcontractor } from 'types/api.graphql'

const SubcontractorName = styled(Typography.Text)`
  display: flex;
  align-items: center;
  gap: 0.3rem;
`

type PdfData = {
  manuallySuppliedSignOns: ManualSignOns
  manuallySuppliedSubcontractors: [Subcontractor]
}

type Props = {
  block: SubcontractorSummaryDisplayBlockType
}

export const SubcontractorSummaryDisplayBlock: React.FC<Props> = ({
  block,
}) => {
  const { value, hourConfig } = block
  const { extraInfo, projectId } = useTemplateRenderer()

  const siteDiaryId = extraInfo.siteDiaryId as string
  const dataForPDF = extraInfo.dataForPDF as PdfData

  const { loading, importedAttendance, projectSubcontractors } =
    useAttendanceData(
      projectId,
      siteDiaryId,
      value,
      hourConfig,
      dataForPDF
        ? {
            includeSubbieBreakdown: false,
            manuallySuppliedSignOns: dataForPDF.manuallySuppliedSignOns,
            manuallySuppliedSubcontractors:
              dataForPDF.manuallySuppliedSubcontractors,
          }
        : {
            includeAutomatedSignOns: false,
            manuallySuppliedSignOns: null,
            includeSubbieBreakdown: true,
            manuallySuppliedSubcontractors: null,
          }
    )

  if (loading) return <Skeleton />

  const valueToUse = value || importedAttendance

  const subbieName = (id: string) =>
    projectSubcontractors.find((s) => s.id === id)?.company?.name || 'Unknown'

  const subbieData = valueToUse?.subcontractorAttendances?.map((s, i1) => {
    return {
      key: `${s.subcontractorId}-${i1}`,
      name: s.adHoc ? s.subcontractorName : subbieName(s.subcontractorId),
      adHoc: s.adHoc,
      hours: block?.value?.subcontractorAttendances?.[
        i1
      ]?.workerAttendance.reduce((acc, curr) => curr.hours, 0),
      totalHours: calculateHours(s, block?.value?.overrides || []),
      workers: s.workerAttendance.length,
      comments: s.comments || '-',
    }
  })

  const subbieColumns: ColumnType<(typeof subbieData)[number]>[] = [
    {
      title: 'Company',
      key: 'name',
      dataIndex: 'name',
      render: (name: string, record) => {
        return (
          <SubcontractorName>
            {name}
            {record.adHoc && (
              <Tooltip title="Manually added subcontractor">
                <IconInfoCircle size={16} color={'#aaa'} />
              </Tooltip>
            )}
          </SubcontractorName>
        )
      },
    },
    { title: 'Workers', key: 'workers', dataIndex: 'workers' },
    {
      title: 'Hours each',
      key: 'hours',
      dataIndex: 'hours',
    },
    {
      title: 'Total',
      key: 'totalHours',
      dataIndex: 'totalHours',
    },
    {
      title: 'Comments',
      key: 'comments',
      dataIndex: 'comments',
    },
  ]

  return (
    <>
      <Table
        pagination={false}
        columns={subbieColumns}
        dataSource={subbieData}
        expandable={{ expandRowByClick: true }}
        footer={() =>
          valueToUse?.subcontractorAttendances && (
            <Typography.Text
              type="secondary"
              style={{
                display: 'block',
                textAlign: 'right',
              }}
            >
              Total hours:{' '}
              {calculateTotalHours(
                valueToUse?.subcontractorAttendances,
                valueToUse?.overrides
              )}
            </Typography.Text>
          )
        }
      />

      {!!valueToUse?.visitors?.length && (
        <div className="ant-form-item-label" style={{ marginTop: '1rem' }}>
          <label htmlFor="visitors">Visitors</label>
          <ul id="visitors">
            {valueToUse.visitors.map((visitor) => (
              <li key={visitor}>{visitor}</li>
            ))}
          </ul>
        </div>
      )}
    </>
  )
}
